import * as React from 'react';

import CoursePageUnitTemplate from '../components/content_hubs/course_page_unit_template'
import { Spinner, } from '../../Shared/index'
import { requestGet, } from '../../../modules/request'
import { LITERATURE_COURSE_PAGE, TEKS_COURSE_PAGE } from '../constants/contentHubs';

interface CoursePageProps {
  overviewSection: JSX.Element,
  localStorageKey: string,
  contentPageLink: JSX.Element,
  partnerSection: JSX.Element,
  unitTemplatePath: string,
  activitiesHeader: string,
  courseType?: string,
  subscriptionType?: string
}

const CoursePage = ({
  overviewSection,
  localStorageKey,
  contentPageLink,
  partnerSection,
  unitTemplatePath,
  activitiesHeader,
  courseType,
  subscriptionType
}: CoursePageProps) => {
  const [unitTemplates, setUnitTemplates] = React.useState(null)
  const [expandedUnitTemplateIds, setExpandedUnitTemplateIds] = React.useState(window.localStorage.getItem(localStorageKey)?.split(',').map(id => Number(id)))

  const scrollPositionKey = `${localStorageKey}_scroll`
  const scrollRestoredRef = React.useRef(false)

  React.useEffect(() => {
    if ('scrollRestoration' in history) { history.scrollRestoration = 'manual' }
    return () => { if ('scrollRestoration' in history) { history.scrollRestoration = 'auto' } }
  }, [])

  React.useEffect(() => {
    const saveScrollPosition = () => { sessionStorage.setItem(scrollPositionKey, String(window.scrollY)) }
    window.addEventListener('pagehide', saveScrollPosition)
    return () => {
      window.removeEventListener('pagehide', saveScrollPosition)
      saveScrollPosition()
    }
  }, [])

  React.useEffect(() => {
    if (!unitTemplates || !expandedUnitTemplateIds || scrollRestoredRef.current) { return }
    scrollRestoredRef.current = true
    const savedPosition = sessionStorage.getItem(scrollPositionKey)
    if (savedPosition !== null) {
      window.scrollTo(0, parseInt(savedPosition, 10))
      sessionStorage.removeItem(scrollPositionKey)
    } else {
      window.scrollTo(0, 0)
    }
  }, [unitTemplates, expandedUnitTemplateIds])

  React.useEffect(() => {
    requestGet(unitTemplatePath,
      ({ unit_templates, }) => { setUnitTemplates(unit_templates) }
    )
  }, [])

  React.useEffect(() => {
    if (expandedUnitTemplateIds || !unitTemplates) { return }

    setExpandedUnitTemplateIds(unitTemplates.map(unitTemplate => unitTemplate.unit_template_id))
  }, [unitTemplates])

  React.useEffect(() => {
    if (!expandedUnitTemplateIds) { return }

    window.localStorage.setItem(localStorageKey, expandedUnitTemplateIds.join(','))
  }, [expandedUnitTemplateIds])

  function expandAllUnitTemplates() {
    setExpandedUnitTemplateIds(unitTemplates.map(ut => ut.unit_template_id))
  }

  function collapseAllUnitTemplates() {
    setExpandedUnitTemplateIds([])
  }

  function toggleUnitTemplateExpansion(unitTemplateId) {
    const newExpandedUnitTemplateIds = expandedUnitTemplateIds.includes(unitTemplateId) ? expandedUnitTemplateIds.filter(id => id !== unitTemplateId) : expandedUnitTemplateIds.concat(unitTemplateId)
    setExpandedUnitTemplateIds(newExpandedUnitTemplateIds)
  }

  if (!unitTemplates || !expandedUnitTemplateIds) {
    return (
      <div className="container content-hub-course-page-container white-background-accommodate-footer">
        {contentPageLink}
        <Spinner />
      </div>
    )
  }

  const unitTemplateElements = unitTemplates?.map(unitTemplate => {
    return (
      <CoursePageUnitTemplate
        courseType={courseType}
        expandedUnitTemplateIds={expandedUnitTemplateIds}
        key={unitTemplate.display_name}
        subscriptionType={subscriptionType}
        toggleUnitTemplateExpansion={toggleUnitTemplateExpansion}
        unitTemplate={unitTemplate}
      />
    )
  })

  return (
    <main className={`container content-hub-course-page-container ${courseType || ''} white-background-accommodate-footer`}>

      {contentPageLink}

      {overviewSection}

      <div className="section-header">
        <h2>{activitiesHeader}</h2>
        <div className="toggle-buttons">
          <button className="quill-button focus-on-light medium grey outlined" onClick={expandAllUnitTemplates} type="button">Expand all</button>
          <button className="quill-button focus-on-light medium grey outlined" onClick={collapseAllUnitTemplates} type="button">Collapse all</button>
        </div>
      </div>

      {unitTemplateElements}

      {partnerSection}
      {courseType === TEKS_COURSE_PAGE && <a className="quill-button teks-bottom-button medium outlined grey focus-on-light" href={`${window.postbuildEnv.DEFAULT_URL}/quill-for-texas`}>Learn More About Quill's TEKS Activity Packs</a>}
      {courseType === LITERATURE_COURSE_PAGE && <a className="quill-button literature-bottom-button medium outlined grey focus-on-light" href={`${window.postbuildEnv.DEFAULT_URL}/literature`}>Learn More About Quill's Literature Activities</a>}
    </main>
  )

}

export default CoursePage
